import { from, Observable, of } from 'rxjs';
import { concatMap, delay, scan } from 'rxjs/operators';

const CHAR_DURATION_MS = 2;

function getDiff(last: string, current: string): string {
    return current.substring(last.length);
}

function typeIt(text: string, charDuration: number = CHAR_DURATION_MS): Observable<string> {
    return from(text).pipe(concatMap((char) => of(char).pipe(delay(charDuration))));
}

/**
 *
 * @param source$ observable - observable that emits a sequence of strings, every string starts with a prefix of the string that came before him.
 * @param charDuration - An optional argument to define the delay duration in milliseconds

 * @returns observable that emits the diff between every source$ emit with delay of charDuration between each character.
 *
 *  * @example
 *
 *     typer(of('abc', 'abc d', 'abc def'))
 *
 *  output:
 *         'a', 'b', 'c'
 *         ' ', 'd'
 *         'e', 'f'
 *
 *  */
export function typer(source$: Observable<string>, charDuration?: number): Observable<string> {
    return source$.pipe(
        scan(
            ({ processed }, latest) => {
                const isValidContinuation = latest.startsWith(processed);
                const nextProcessed = isValidContinuation ? latest : '';
                const remaining = isValidContinuation ? getDiff(processed, latest) : '';

                return {
                    processed: nextProcessed,
                    remaining
                };
            },
            { processed: '', remaining: '' }
        ),
        concatMap(({ remaining }) => typeIt(remaining, charDuration)),
        scan((all, current) => all + current)
    );
}
